import { useEffect, useRef, useState } from 'react';

const features = [
  {
    icon: (
      <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
      </svg>
    ),
    title: '一人ひとりに寄り添うケア',
    description:
      'ご利用者の生活歴や好み、体調を丁寧に把握し、個別ケア計画を作成します。画一的なサービスではなく、その方らしい一日を大切にしています。',
    image: 'https://readdy.ai/api/search-image?query=Japanese%20elderly%20care%20staff%20gently%20holding%20hands%20with%20senior%20woman%20warm%20indoor%20daylight%20soft%20beige%20interior%20professional%20caregiver%20uniform%20compassionate%20expression%20natural%20light%20clean%20facility%20peaceful%20moment&width=600&height=800&seq=feat001&orientation=portrait',
  },
  {
    icon: (
      <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
        <circle cx="9" cy="7" r="4" />
        <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
        <path d="M16 3.13a4 4 0 0 1 0 7.75" />
      </svg>
    ),
    title: '専門資格を持つスタッフ体制',
    description:
      '介護福祉士・看護師・理学療法士など、専門資格を持つスタッフが連携してサポートします。定期的な研修で知識と技術の向上に努めています。',
    image: 'https://readdy.ai/api/search-image?query=Japanese%20professional%20care%20staff%20team%20meeting%20bright%20office%20room%20diverse%20caregivers%20uniforms%20smiling%20collaborative%20discussion%20clean%20modern%20facility%20natural%20window%20light%20warm%20atmosphere%20group%20photo&width=600&height=800&seq=feat002&orientation=portrait',
  },
  {
    icon: (
      <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="10" />
        <polyline points="12 6 12 12 16 14" />
      </svg>
    ),
    title: '安心の送迎・健康管理',
    description:
      '専用車両による自宅への送迎サービスを実施。毎日のバイタルチェックや服薬管理など、健康面のサポートも丁寧に行っています。',
    image: 'https://readdy.ai/api/search-image?query=Japanese%20elderly%20care%20van%20transportation%20service%20staff%20helping%20senior%20get%20in%20vehicle%20suburban%20neighborhood%20morning%20light%20clean%20white%20minivan%20professional%20driver%20uniform%20safe%20gentle%20assistance&width=600&height=800&seq=feat003&orientation=portrait',
  },
];

function useInView(threshold = 0.15) {
  const ref = useRef<HTMLDivElement>(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setInView(true); },
      { threshold }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [threshold]);
  return { ref, inView };
}

export default function FeaturesSection() {
  const { ref, inView } = useInView();

  return (
    <section id="features" className="py-24 md:py-32 bg-white">
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        {/* Header */}
        <div ref={ref} className="text-center mb-16 md:mb-20">
          <div
            className={`inline-flex items-center gap-2 mb-4 transition-all duration-700 ${
              inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
            }`}
          >
            <div className="h-px w-8 bg-[#88B277]" />
            <span className="text-[#88B277] text-sm font-semibold tracking-widest uppercase">Features</span>
            <div className="h-px w-8 bg-[#88B277]" />
          </div>
          <h2
            className={`text-3xl md:text-4xl font-bold text-gray-800 mb-4 transition-all duration-700 delay-100 ${
              inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
            }`}
          >
            さくらデイサービスの<span className="text-[#88B277]">3つの特徴</span>
          </h2>
          <p
            className={`text-gray-500 text-base max-w-xl mx-auto leading-relaxed transition-all duration-700 delay-200 ${
              inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
            }`}
          >
            ご利用者とそのご家族が安心して任せられるよう、
            日々のケアの質と環境づくりに真摯に取り組んでいます。
          </p>
        </div>

        {/* Feature Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
          {features.map((feature, i) => (
            <FeatureCard key={feature.title} feature={feature} index={i} parentInView={inView} />
          ))}
        </div>
      </div>
    </section>
  );
}

function FeatureCard({
  feature,
  index,
  parentInView,
}: {
  feature: (typeof features)[0];
  index: number;
  parentInView: boolean;
}) {
  const [hovered, setHovered] = useState(false);

  return (
    <div
      className={`group relative overflow-hidden rounded-2xl cursor-pointer transition-all duration-700 ${
        parentInView
          ? `opacity-100 translate-y-0 scale-100`
          : 'opacity-0 translate-y-8 scale-95'
      }`}
      style={{ transitionDelay: `${300 + index * 150}ms` }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      {/* Image */}
      <div className="relative h-80 md:h-96 overflow-hidden">
        <img
          src={feature.image}
          alt={feature.title}
          className={`w-full h-full object-cover object-top transition-transform duration-700 ${
            hovered ? 'scale-110' : 'scale-100'
          }`}
        />
        {/* Gradient Overlay */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/75 via-black/20 to-transparent" />

        {/* Icon */}
        <div className="absolute top-5 left-5 w-12 h-12 flex items-center justify-center bg-white/20 backdrop-blur-sm rounded-xl text-white border border-white/30">
          {feature.icon}
        </div>

        {/* Text */}
        <div className="absolute bottom-0 left-0 right-0 p-6">
          <h3 className="text-white font-bold text-lg mb-2 leading-snug">{feature.title}</h3>
          <p
            className={`text-white/85 text-sm leading-relaxed transition-all duration-500 ${
              hovered ? 'opacity-100 max-h-32' : 'opacity-0 max-h-0'
            } overflow-hidden`}
          >
            {feature.description}
          </p>
        </div>
      </div>

      {/* Bottom Description (always visible on mobile) */}
      <div className="md:hidden p-5 bg-gray-50">
        <p className="text-gray-600 text-sm leading-relaxed">{feature.description}</p>
      </div>
    </div>
  );
}
